import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import {
  gamesQuery,
  getClassificationScores,
  getNormalScores,
  getPrinciplesScores,
  getBalancedEquationScores,
} from '../api';

import { navigation } from '../data';
import { tableHeaders, gameTypes } from './data';
import SubPagesLayout from '../../UI/layout/subPages';

import Table from '../../UI/table';

export default function Points() {
  const navigate = useNavigate();
  const [tableData, setTableData] = useState([]);
  const [totalPoints, setTotalPoints] = useState(null);

  const { data: games } = useQuery(gamesQuery()) || {};
  
  const { data: classificationScores } = useQuery({
    queryKey: ['classificationScoresOverall'],
    queryFn: async () => getClassificationScores({completed: true}),
    refetchOnWindowFocus: false,
  });

  const { data: normalScores } = useQuery({
    queryKey: ['normalScoresOverall'],
    queryFn: async () => getNormalScores({completed: true}),
    refetchOnWindowFocus: false,
  });

  const { data: balancedEquationScores } = useQuery({
    queryKey: ['equationScoresOverall'],
    queryFn: async () => getBalancedEquationScores({completed: true}),
    refetchOnWindowFocus: false,
  });

  const { data: principlesScores } = useQuery({
    queryKey: ['principlesScoresOverall'],
    queryFn: async () => getPrinciplesScores({completed: true}),
    refetchOnWindowFocus: false,
  });

  const calculatePointsSums = (scores) => {
    if (!scores || scores.length === 0) return {};

    const grouped = scores.reduce((acc, score) => {
      const key = `${score.game}-${score.attempt}`;
      if (!acc[key]) {
        acc[key] = {
          game: score.game,
          attempt: score.attempt,
          points: [],
        };
      }
      acc[key].points.push(score.total_points);
      return acc;
    }, {});

    const sums = {};
    Object.values(grouped).forEach(({ game, attempt, points }) => {
      const key = `${game}-${attempt}`;
      const sum = points.reduce((a, b) => a + b, 0);
      sums[key] = sum;
    });

    return sums;
  };

  const findHighestAttempts = (pointsSums) => {
    const gameHighest = {};
    
    Object.entries(pointsSums).forEach(([key, points]) => {
      const [gameId] = key.split('-');
      
      if (!gameHighest[gameId] || points > gameHighest[gameId].points) {
        gameHighest[gameId] = { points, key };
      }
    });
    
    return gameHighest;
  };

  const calculateTotalPoints = (highestAttempts) => {
    const highestPoints = Object.values(highestAttempts).map(item => item.points);
    
    if (highestPoints.length === 0) return null;
    
    const total = highestPoints.reduce((a, b) => a + b, 0);
    
    return total.toLocaleString();
  };

  useEffect(() => {
    if (games && classificationScores && normalScores && balancedEquationScores && principlesScores) {
      const allScores = [
        ...(classificationScores || []),
        ...(normalScores || []),
        ...(balancedEquationScores || []),
        ...(principlesScores || [])
      ];

      const pointsSums = calculatePointsSums(allScores);
      const highestAttempts = findHighestAttempts(pointsSums);

      const total = calculateTotalPoints(highestAttempts);
      setTotalPoints(total);

      const maxAttempts = Math.max(
        ...Object.keys(pointsSums).map(key => parseInt(key.split('-')[1])),
        3
      );

      const data = games.map(game => {
        const cells = [];
        
        cells.push({
          value: `${game.name} (${gameTypes[game.game_type]})`,
          style: {}
        });
        
        for (let attempt = 1; attempt <= maxAttempts; attempt++) {
          const key = `${game.id}-${attempt}`;
          const points = pointsSums[key];
          const isHighest = highestAttempts[game.id]?.key === key;
          
          cells.push({
            value: points !== undefined ? points.toLocaleString() : '--',
            style: isHighest ? { backgroundColor: '#EAF3EC' } : {}
          });
        }
        
        return { cells };
      });

      setTableData(data);
    }
  }, [games, classificationScores, normalScores, balancedEquationScores, principlesScores]);

  const handlePrevButtonClick = () => {
    navigate('/insights/decision-insights/principles/');
  }
  
  const handleNextButtonClick = () => {
    navigate('/insights/points-scores/scores' );
  }

  return (
    <SubPagesLayout
      navigation={navigation}
      onFirstButtonClick={handlePrevButtonClick}
      onSecondButtonClick={handleNextButtonClick}
      currentPage={1}
      pagesQuantity={1}
      contentClassName="decision-insights-content-container"
    >
      <h1>Points & scores</h1>
      <h2>Total Points</h2>
      <p>
        You've accumulated <b>{totalPoints !== null ? totalPoints : '--'}</b>. 
        This is calculated by adding up the highest points (highlighted in green) from each completed assignment.
      </p>
      
      <Table headers={tableHeaders} rows={tableData} />
    </SubPagesLayout>
  );
}